import React from 'react';
import { Layers, Bot, Coins } from 'lucide-react';

// Binance COIN-M (salt okunur) — Pozisyonlar sayfasının "COIN-M" sekmesi.
//
// Kullanıcı kararı (30/09/2026): USD cinsinden PnL GÖSTERİLMEZ. COIN-M kârı coin
// olarak tutuluyor; dolara ancak etiketlenmiş bir swap ile dönüşür ve o swap kendi
// işlem satırında görünür. Burada her tutar kendi coin'i cinsinden.
// Mark/giriş FİYATI ise coin'in USD fiyatıdır — bu bir çevrim değil, sözleşmenin fiyatı.

interface Props {
  dark?: boolean;
  t: any;          // getTheme(dark)
  card: string;
  coinm: any;      // binance_cm.poll_state çıktısı
  connected: boolean;
}

const fmtC = (v: number, d = 8) =>
  (v >= 0 ? '+' : '') + v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: d });
const fmtN = (v: number, d = 8) => v.toLocaleString('tr-TR', { maximumFractionDigits: d });
const fmtP = (v: number) => v.toLocaleString('tr-TR', { maximumFractionDigits: 2 });
const fmtDate = (ms: number) => new Date(ms).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
const days = (ms: number) => Math.max(0, (Date.now() - ms) / 86400000);

export default function CoinMPanel({ t, card, coinm, connected }: Props) {
  const positions: any[] = coinm?.positions || [];
  const balances: any[] = coinm?.balances || [];
  const grid: any[] = coinm?.grid || [];

  // Gerçekleşmemiş PnL COIN BAŞINA toplanır — ETH ile BTC toplanamaz.
  const upnlByCoin: Record<string, number> = {};
  for (const p of positions) upnlByCoin[p.coin] = (upnlByCoin[p.coin] || 0) + p.unrealizedPnl;

  if (!coinm) {
    return (
      <div className={`${card} p-8 text-center ${t.muted} text-sm`}>
        {connected ? 'COIN-M verisi bekleniyor…' : 'Backend bağlantısı bekleniyor...'}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Bakiyeler — coin başına */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        {balances.length === 0 ? (
          <div className={`${card} p-4 ${t.muted} text-sm`}>COIN-M hesabında bakiye yok.</div>
        ) : balances.map(b => (
          <div key={b.asset} className={`${card} p-4`}>
            <p className={`text-xs mb-1 ${t.muted} flex items-center gap-1.5`}><Coins size={12} /> {b.asset} bakiye</p>
            <p className={`text-xl font-bold ${t.text} font-mono`}>{fmtN(b.walletBalance)} {b.asset}</p>
            <p className={`text-[11px] mt-1 ${t.muted}`}>
              Kullanılabilir {fmtN(b.availableBalance)}
              {b.unrealizedPnl !== 0 && (
                <> · gerçekleşmemiş <span className={b.unrealizedPnl >= 0 ? t.positive : t.negative}>
                  {fmtC(b.unrealizedPnl)}</span></>
              )}
            </p>
          </div>
        ))}
      </div>

      {/* Çalışan grid'ler — kara kutu. Kapananlar burada GÖSTERİLMEZ:
          İşlem Geçmişi → COIN-M → "Grid'ler" kartına taşınır (kullanıcı kararı). */}
      {grid.length > 0 && (
        <div className="space-y-2">
          {grid.map(g => (
            <div key={g.id} className={`${card} p-4 border-l-4 border-l-amber-500`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Bot size={15} className="text-amber-400" />
                  <span className={`font-semibold text-sm ${t.text}`}>{g.pair} Grid</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 bg-amber-500/10">
                    çalışıyor
                  </span>
                </div>
                <span className={`text-[11px] ${t.muted}`}>
                  {fmtDate(g.openTs)} · {days(g.openTs).toFixed(1)} gündür
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                <div><p className={t.muted}>Deposit</p><p className={`font-mono ${t.text}`}>{fmtN(g.deposit)} {g.asset}</p></div>
                <div><p className={t.muted}>Withdraw</p><p className={`font-mono ${t.text}`}>{fmtN(g.withdraw)} {g.asset}</p></div>
                <div><p className={t.muted}>Net margin</p><p className={`font-mono font-semibold ${t.text}`}>{fmtN(g.inStrategy)} {g.asset}</p></div>
              </div>
              {/* Döküm: açılış ve margin hareketleri ayrı (yalnız hareket varsa) */}
              {(g.added > 0 || g.removed > 0) && (
                <p className={`text-[11px] mt-1.5 font-mono ${t.muted}`}>
                  {fmtN(g.initial)} açılış
                  {g.added > 0 && <> + {fmtN(g.added)} eklenen</>}
                  {g.removed > 0 && <> − {fmtN(g.removed)} çıkarılan</>}
                  {' '}= {fmtN(g.inStrategy)} {g.asset}
                </p>
              )}
              {/* Neden sadece transfer: Binance grid'i ayrı bir strateji hesabında
                  çalıştırıyor; dolumları ve pozisyonu API'nin ana hesap uçlarında YOK. */}
              <p className={`text-[10px] mt-2 ${t.muted}`}>
                Grid ayrı strateji hesabında çalışır; dolumları API'de görünmez. Net PnL bot kapanınca kesinleşir ve İşlem Geçmişi → COIN-M'e taşınır.
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Açık pozisyonlar */}
      <div className="grid grid-cols-2 gap-4">
        <div className={`${card} p-4`}>
          <p className={`text-xs mb-1 ${t.muted}`}>Gerçekleşmemiş PnL</p>
          {Object.keys(upnlByCoin).length === 0
            ? <p className={`text-xl font-bold ${t.muted}`}>—</p>
            : Object.entries(upnlByCoin).map(([c, v]) => (
                <p key={c} className={`text-xl font-bold font-mono ${v >= 0 ? t.positive : t.negative}`}>{fmtC(v)} {c}</p>
              ))}
        </div>
        <div className={`${card} p-4`}>
          <p className={`text-xs mb-1 ${t.muted}`}>Açık Pozisyon</p>
          <p className={`text-xl font-bold ${t.text}`}>{positions.length}</p>
        </div>
      </div>

      {positions.length === 0 ? (
        <div className={`${card} p-8 text-center ${t.muted} text-sm`}>
          Ana COIN-M hesabında açık pozisyon yok.{grid.length > 0 && ' (Grid pozisyonları strateji hesabında, yukarıdaki kartta.)'}
        </div>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-xs">
            <thead>
              <tr className={`${t.tableHeader} border-b`}>
                {['Sembol', 'Yön', 'Kontrat', 'Giriş', 'Mark', 'Gerçekleşmemiş PnL', 'Kaldıraç', 'Likidasyon'].map(h => (
                  <th key={h} className={`px-3 py-2.5 text-left font-medium ${t.muted}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {positions.map(p => (
                <tr key={p.symbol} className={`border-b ${t.tableRow}`}>
                  <td className={`px-3 py-2.5 font-semibold ${t.text}`}>
                    <span className="flex items-center gap-1.5"><Layers size={12} className={t.muted} />{p.symbol}</span>
                  </td>
                  <td className={`px-3 py-2.5 font-semibold ${p.side === 'LONG' ? t.positive : t.negative}`}>{p.side}</td>
                  <td className={`px-3 py-2.5 font-mono ${t.text}`}>
                    {fmtN(p.contracts, 0)}
                    {/* Kontrat NOMİNALİ sözleşmenin tanımıdır (PnL çevrimi değil) */}
                    {p.contractSize > 0 && <span className={`${t.muted} text-[10px]`}> · {fmtN(p.contracts * p.contractSize, 0)} USD nominal</span>}
                  </td>
                  <td className={`px-3 py-2.5 font-mono ${t.text}`}>{fmtP(p.entryPrice)}</td>
                  <td className={`px-3 py-2.5 font-mono ${t.text}`}>{fmtP(p.markPrice)}</td>
                  <td className={`px-3 py-2.5 font-mono font-semibold ${p.unrealizedPnl >= 0 ? t.positive : t.negative}`}>
                    {fmtC(p.unrealizedPnl)} {p.coin}
                  </td>
                  <td className={`px-3 py-2.5 ${t.text}`}>{p.leverage}x <span className={t.muted}>{p.marginType}</span></td>
                  <td className={`px-3 py-2.5 font-mono ${t.muted}`}>{p.liquidationPrice > 0 ? fmtP(p.liquidationPrice) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
